import { createSelector } from 'reselect';

import {
  FavoritePhotoPayload,
  FavoriteStorefrontPayload,
} from '~/actions/types/favoriteActionTypes';
import type { RootState } from '~/reducers';
import type { FavoritePhotosState, FavoriteStorefrontsState } from '~/reducers/entities/favorites';

/** return the raw state slice for use with createSelector for performance and preventing re-rendering  */
const getFavoritePhotosEntity = (state: RootState): FavoritePhotosState =>
  state.entities.favorites['wedding-photos'];

export const hasFavoritePhoto = createSelector(getFavoritePhotosEntity, (stateSlice) =>
  stateSlice.loaded ? stateSlice.allIds.length > 0 : null
);

const getFavoritePhotoLookup = createSelector(
  getFavoritePhotosEntity,
  (stateSlice) => stateSlice.byId
);

// The type is needed because the result can be undefined and TS is not inferring that.
const getFavoritePhoto = (imageId: string) =>
  createSelector(
    getFavoritePhotoLookup,
    (lookup): FavoritePhotoPayload | undefined => lookup[imageId]
  );

export const getFavoritePhotoBoardUuid = createSelector(
  getFavoritePhotosEntity,
  (stateSlice) => (stateSlice.boards ? stateSlice.boards[0] : undefined) // this is better than boards[0] because ts infers that the result can be undefined
);

export const getFavoritePhotosLoaded = createSelector(
  getFavoritePhotosEntity,
  (stateSlice) => stateSlice.loaded
);

/** return the raw state slice for use with createSelector for performance and preventing re-rendering  */
export const getFavoriteStorefrontEntity = (state: RootState): FavoriteStorefrontsState =>
  state.entities.favorites['storefront-listings'];

const getFavoriteStorefrontLookup = createSelector(
  getFavoriteStorefrontEntity,
  (stateSlice) => stateSlice.byId
);

export const getFavoriteStorefront = (storefrontUuid: string) =>
  createSelector(
    getFavoriteStorefrontLookup,
    (lookup): FavoriteStorefrontPayload | undefined => lookup[storefrontUuid]
  );

export const getFavoriteStorefrontsLoaded = createSelector(
  getFavoriteStorefrontEntity,
  (stateSlice) => stateSlice.loaded
);

/**
 * Gets a favorite item, either a storefront or a photo
 *
 * This might need a different type definition. Specifically, if we've fetched
 * favorites from the backend, we have BoardStorefrontViews and BoardWeddingPhotoView
 * in redux.   But as we favorite and unfavorite things, we put partial objects
 * in the state (just the imageId or the storefrontUuid, which we've defined as
 * FavoritePhotoPayload and FavoriteStorefrontPayload).  This is only relevant if we fail
 * to favorite or unfavorite something (as we rollback to a previous state with a partial object
 * instead of a full object).  Note: we want an immediately responsive UI
 * without waiting for API Call => Redux Change => Selector change to show if a
 * photo or storefront is favorited, which is why we put in a partial object sometimes.
 */
export const getFavoriteItem = (
  item: FavoritePhotoPayload & FavoriteStorefrontPayload,
  isVendor: boolean
): ((state: RootState) => FavoriteStorefrontPayload | FavoritePhotoPayload | undefined) => {
  if (item.imageId && !isVendor) {
    return getFavoritePhoto(item.imageId);
  }
  return getFavoriteStorefront(item.storefrontUuid);
};

export const getMostRecentStorefrontFavorite = (state: RootState) => {
  const favoriteStorefronts = getFavoriteStorefrontEntity(state)?.favoriteStorefronts || [];
  if (favoriteStorefronts.length) {
    return favoriteStorefronts.reduce((a, b) => (a.createdAt > b.createdAt ? a : b));
  }
  return null;
};
